import { CheckmarkCircle01Icon, Copy01Icon, LinkSquare02Icon, Location01Icon, StarIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Rating,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/hooks";
import useSnackbar from "@/hooks/useSnackbar";
import { updateOrganization } from "@/features/organizations/slice";
import { useFetchOrganizationsByIdQuery } from "@/features/organizations/api/organizationsApi";
import { useSaveSurveyGoogleReviewsConfigMutation } from "@/api/surveysApi";
import { Question, Survey, SurveyGoogleReviewsConfig } from "@/types/surveys";
import { skipToken } from "@reduxjs/toolkit/dist/query";

export const buildGoogleWriteReviewUrl = (placeId: string): string => {
  const clean = placeId.trim();
  return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(clean)}`;
};

export const resolveSurveyGoogleReviewsDefaults = (
  survey?: Survey | null
): {
  ratingQuestions: Question[];
  textQuestions: Question[];
  resolvedConfig: SurveyGoogleReviewsConfig;
} => {
  const questions = survey?.questions ?? [];
  const ratingQuestions = questions.filter((q) => q.type === "rating");
  const textQuestions = questions.filter((q) => q.type === "text");

  const saved = survey?.googleReviews ?? null;

  const fixedRatingId =
    ratingQuestions.find((q) => q.id === "q_overall_rating")?.id ??
    ratingQuestions[0]?.id ??
    null;
  const fixedCommentId =
    textQuestions.find((q) => q.id === "q_comments")?.id ??
    textQuestions[0]?.id ??
    null;

  return {
    ratingQuestions,
    textQuestions,
    resolvedConfig: {
      enabled: saved?.enabled ?? true,
      ratingQuestionId: fixedRatingId,
      commentQuestionId: fixedCommentId,
      minRating: saved?.minRating ?? 1,
    },
  };
};

export const formatGoogleReviewClipboardText = (
  ratingValue?: number | null,
  commentText?: string | null
): string => {
  const cleanComment = (commentText ?? "").trim();
  const hasValidRating =
    typeof ratingValue === "number" && !Number.isNaN(ratingValue) && ratingValue >= 1;

  if (cleanComment && hasValidRating) {
    const stars = "★".repeat(Math.round(ratingValue));
    return `${cleanComment}\n\nCalificación: ${stars} (${ratingValue}/5)`;
  }
  if (cleanComment) {
    return cleanComment;
  }
  if (hasValidRating) {
    const stars = "★".repeat(Math.round(ratingValue));
    return `Calificación: ${stars} (${ratingValue}/5)`;
  }
  return "";
};

interface SurveyGoogleReviewsDialogProps {
  open: boolean;
  onClose: () => void;
  survey: Survey | null;
  organizationId?: string;
  currentAnswers?: Record<string, string | string[] | number>;
  onSaved?: (config: SurveyGoogleReviewsConfig, placeId: string) => void;
}

const SurveyGoogleReviewsDialog = ({
  open,
  onClose,
  survey,
  organizationId,
  currentAnswers,
  onSaved,
}: SurveyGoogleReviewsDialogProps) => {
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar();

  const reduxOrg = useAppSelector((state) =>
    organizationId ? state.organizations?.[organizationId] : null
  );
  const currentUser = useAppSelector((state) => state.auth?.user);

  const orgQuery = useFetchOrganizationsByIdQuery(organizationId || skipToken, {
    skip: !open || Boolean(reduxOrg) || !organizationId,
  });

  const organization = reduxOrg || orgQuery.data;

  const existingPlaceId = useMemo(
    () => (organization?.googlePlaceId || "").trim(),
    [organization?.googlePlaceId]
  );

  const { ratingQuestions, textQuestions, resolvedConfig } = useMemo(
    () => resolveSurveyGoogleReviewsDefaults(survey),
    [survey]
  );

  const [placeId, setPlaceId] = useState(existingPlaceId);
  const [isEditingPlaceId, setIsEditingPlaceId] = useState(!existingPlaceId);
  const [isSavingPlaceIdOnly, setIsSavingPlaceIdOnly] = useState(false);

  const [enabled, setEnabled] = useState<boolean>(resolvedConfig.enabled);
  const [ratingQuestionId, setRatingQuestionId] = useState<string>(
    resolvedConfig.ratingQuestionId ?? ""
  );
  const [commentQuestionId, setCommentQuestionId] = useState<string>(
    resolvedConfig.commentQuestionId ?? ""
  );
  const [minRating, setMinRating] = useState<number>(resolvedConfig.minRating ?? 1);

  const [previewRating, setPreviewRating] = useState<number>(5);
  const [previewComment, setPreviewComment] = useState<string>("");

  const [saveConfig, saveConfigState] = useSaveSurveyGoogleReviewsConfigMutation();

  useEffect(() => {
    if (!open) return;
    setPlaceId(existingPlaceId);
    setIsEditingPlaceId(!existingPlaceId);
    setEnabled(resolvedConfig.enabled);
    setRatingQuestionId(resolvedConfig.ratingQuestionId ?? "");
    setCommentQuestionId(resolvedConfig.commentQuestionId ?? "");
    setMinRating(resolvedConfig.minRating ?? 1);
  }, [open, existingPlaceId, resolvedConfig]);

  useEffect(() => {
    if (!open) return;
    const rawRating = ratingQuestionId ? currentAnswers?.[ratingQuestionId] : undefined;
    const numRating = Number(rawRating);
    if (!Number.isNaN(numRating) && numRating >= 1 && numRating <= 5) {
      setPreviewRating(numRating);
    } else {
      setPreviewRating(5);
    }

    const rawComment = commentQuestionId ? currentAnswers?.[commentQuestionId] : undefined;
    if (typeof rawComment === "string" && rawComment.trim()) {
      setPreviewComment(rawComment);
    } else {
      setPreviewComment("");
    }
  }, [open, ratingQuestionId, commentQuestionId, currentAnswers]);

  if (!survey) return null;

  const selectedRatingQuestion = ratingQuestions.find((q) => q.id === ratingQuestionId);
  const selectedTextQuestion = textQuestions.find((q) => q.id === commentQuestionId);

  const handleSavePlaceIdInline = async () => {
    const trimmed = placeId.trim();
    if (!trimmed) {
      openSnackbar("Ingresa un Place ID válido para continuar", "warning");
      return;
    }
    if (!organizationId) return;

    setIsSavingPlaceIdOnly(true);
    try {
      await dispatch(
        updateOrganization({
          id: organizationId,
          googlePlaceId: trimmed,
        })
      ).unwrap();
      setIsEditingPlaceId(false);
      openSnackbar("Place ID guardado en el comercio exitosamente", "success");
    } catch {
      openSnackbar("No se pudo guardar el Place ID. Verifica tus permisos.", "error");
    } finally {
      setIsSavingPlaceIdOnly(false);
    }
  };

  const handleSaveAll = async (redirectAfterSave = false) => {
    const trimmedPlaceId = placeId.trim();
    if (!trimmedPlaceId) {
      setIsEditingPlaceId(true);
      openSnackbar(
        "Por favor ingresa el Place ID de Google Places de tu comercio",
        "warning"
      );
      return;
    }

    const configToSave: SurveyGoogleReviewsConfig = {
      enabled,
      ratingQuestionId: ratingQuestionId || null,
      commentQuestionId: commentQuestionId || null,
      minRating,
    };

    if (organizationId && survey.id && currentUser?.uid) {
      try {
        await saveConfig({
          organizationId,
          surveyId: survey.id,
          googlePlaceId: trimmedPlaceId,
          googleReviews: configToSave,
        }).unwrap();

        setIsEditingPlaceId(false);
        onSaved?.(configToSave, trimmedPlaceId);

        if (!redirectAfterSave) {
          openSnackbar(
            "Integración con Google Reviews guardada exitosamente",
            "success"
          );
          onClose();
          return;
        }
      } catch {
        if (!redirectAfterSave) {
          openSnackbar("Error al guardar la configuración de Google Reviews", "error");
          return;
        }
      }
    }

    if (redirectAfterSave) {
      const clipboardPayload = formatGoogleReviewClipboardText(
        previewRating,
        previewComment
      );
      if (clipboardPayload) {
        try {
          await navigator.clipboard.writeText(clipboardPayload);
          openSnackbar(
            "Reseña y puntaje copiados al portapapeles. ¡Pégalos en Google Reviews!",
            "success"
          );
        } catch {
          // Ignorar si el navegador bloquea el portapapeles
        }
      }
      window.open(
        buildGoogleWriteReviewUrl(trimmedPlaceId),
        "_blank",
        "noopener,noreferrer"
      );
      onClose();
    }
  };

  const handleCopyOnly = async () => {
    const clipboardPayload = formatGoogleReviewClipboardText(
      previewRating,
      previewComment
    );
    if (!clipboardPayload) {
      openSnackbar("Escribe un comentario o selecciona un puntaje para copiar", "info");
      return;
    }
    try {
      await navigator.clipboard.writeText(clipboardPayload);
      openSnackbar("Texto y puntaje copiados al portapapeles", "success");
    } catch {
      openSnackbar("No se pudo copiar al portapapeles", "error");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: "rgba(250, 175, 0, 0.15)",
              color: "#d97706",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <HugeiconsIcon icon={StarIcon} size={20} fill="#f59e0b" color="#d97706" />
          </Box>
          <Box>
            <Typography variant="h4">Redirigir a Google Reviews</Typography>
            <Typography variant="caption" color="text.secondary">
              Vincula tu Place ID y configura el asistente de copiado para "{survey.title}"
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Paso 1: Verificación / Guardado de Place ID de la organización */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              borderColor: existingPlaceId && !isEditingPlaceId ? "success.light" : "warning.main",
              bgcolor:
                existingPlaceId && !isEditingPlaceId
                  ? "rgba(34, 197, 94, 0.04)"
                  : "rgba(245, 158, 11, 0.04)",
            }}
          >
            <Stack spacing={1.5}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <HugeiconsIcon icon={Location01Icon} size={18} color={existingPlaceId ? "#16a34a" : "#d97706"} />
                  <Typography variant="subtitle1" fontWeight={700}>
                    1. Google Place ID del comercio
                  </Typography>
                </Stack>

                {existingPlaceId && !isEditingPlaceId ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      color="success"
                      icon={<HugeiconsIcon icon={CheckmarkCircle01Icon} size={14} />}
                      label="Vinculado"
                    />
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setIsEditingPlaceId(true)}
                    >
                      Cambiar
                    </Button>
                  </Stack>
                ) : null}
              </Stack>

              {!existingPlaceId && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  Tu comercio aún no tiene un <strong>Place ID</strong> asociado. Ingrésalo y
                  guárdalo aquí mismo sin tener que cambiar de pantalla.
                </Alert>
              )}

              {existingPlaceId && !isEditingPlaceId ? (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                >
                  <Typography variant="body2" color="text.secondary">
                    Place ID activo: <strong>{existingPlaceId}</strong>
                  </Typography>
                  <MuiLink
                    href={buildGoogleWriteReviewUrl(existingPlaceId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                     sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 13 }}
                  >
                    Probar enlace directo <HugeiconsIcon icon={LinkSquare02Icon} size={13} />
                  </MuiLink>
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Google Place ID"
                      placeholder="Ej: ChIJN1t_tDeuEmsRUsoyG83frY4"
                      value={placeId}
                      onChange={(e) => setPlaceId(e.target.value)}
                    />
                    {currentUser?.uid && (
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={isSavingPlaceIdOnly || !placeId.trim()}
                        onClick={handleSavePlaceIdInline}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {isSavingPlaceIdOnly ? "Guardando..." : "Guardar Place ID"}
                      </Button>
                    )}
                    {existingPlaceId && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setPlaceId(existingPlaceId);
                          setIsEditingPlaceId(false);
                        }}
                      >
                        Cancelar
                      </Button>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    ¿No conoces tu Place ID? Encuéntralo buscando el nombre de tu negocio en el{" "}
                    <MuiLink
                      href="https://developers.google.com/maps/documentation/places/web-service/place-id#find-with-place-id-finder"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Buscador oficial de Place ID de Google
                    </MuiLink>
                    .
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Paper>

          {/* Paso 2: Regla de invitación a Google Reviews */}
          <Stack spacing={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="subtitle1" fontWeight={700}>
                2. Configuración de invitación
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    color="success"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                  />
                }
                label="Mostrar al finalizar encuesta"
              />
            </Stack>

            <Typography variant="body2" color="text.secondary">
              El asistente tomará automáticamente la calificación general y el comentario del
              cliente para que pueda pegarlos en Google Reviews con un clic.
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel>Invitar a Google Reviews cuando la calificación sea</InputLabel>
              <Select
                label="Invitar a Google Reviews cuando la calificación sea"
                value={minRating}
                onChange={(e) => setMinRating(Number(e.target.value))}
              >
                <MenuItem value={1}>Cualquier calificación (1 a 5 ★)</MenuItem>
                <MenuItem value={4}>4 o 5 estrellas (Recomendado)</MenuItem>
                <MenuItem value={5}>Solo 5 estrellas (5 ★)</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Divider />

          {/* Paso 3: Asistente de copiado y envío a Google Reviews */}
          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, bgcolor: "#f8fafc" }}
          >
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" fontWeight={700}>
                Asistente de Copiado para Google Reviews
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Al ir a Google Reviews se copiará el texto junto con el puntaje seleccionado para
                que el cliente solo tenga que pegarlo:
              </Typography>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="body2" fontWeight={600}>
                  {selectedRatingQuestion?.title || "Puntaje"}:
                </Typography>
                <Rating
                  size="medium"
                  value={previewRating}
                  onChange={(_, val) => {
                    if (val !== null) setPreviewRating(val);
                  }}
                />
                <Chip size="small" label={`${previewRating}/5 ★`} color="warning" />
              </Stack>

              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label={
                  selectedTextQuestion
                    ? `Texto a copiar (${selectedTextQuestion.title})`
                    : "Comentario para copiar a Google Reviews"
                }
                placeholder="Escribe o edita el texto que se copiará al portapapeles..."
                value={previewComment}
                onChange={(e) => setPreviewComment(e.target.value)}
                sx={{ bgcolor: "#fff" }}
              />

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<HugeiconsIcon icon={Copy01Icon} size={15} />}
                  onClick={handleCopyOnly}
                >
                  Copiar texto y puntaje
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Cerrar
        </Button>

        <Stack direction="row" spacing={1}>
          {currentUser?.uid && (
            <Button
              variant="outlined"
              color="primary"
              disabled={saveConfigState.isLoading}
              onClick={() => handleSaveAll(false)}
            >
              {saveConfigState.isLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Guardar configuración"
              )}
            </Button>
          )}
          <Button
            variant="contained"
            color="warning"
            endIcon={<HugeiconsIcon icon={LinkSquare02Icon} size={16} />}
            disabled={saveConfigState.isLoading}
            onClick={() => handleSaveAll(true)}
          >
            Copiar e ir a Google Reviews
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default SurveyGoogleReviewsDialog;
